import { FilterParams } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { MouseEvent, PropsWithChildren, ReactElement } from 'react';
import {
    PoolAccordionDetails,
    PoolAccordionSummary,
    PoolButton,
    PoolDrawer,
    PoolIconButton,
    PoolRow,
} from '@synergy/components-next';
import { SortingArrowLeftIcon } from '@synergy/theme-next';
import { styled } from '@mui/material';
import { useSearchParams } from 'react-router-dom';

const StyledPoolButton = styled(PoolButton)(({ theme }) => ({
    '&&.Mui-disabled': {
        color: theme.palette.grey[850],
        borderColor: theme.palette.grey[850],
    },
}));

export const StyledPoolAccordionDetails = styled(PoolAccordionDetails)(({ theme }) => ({
    padding: 0,
    margin: 0,
    marginBottom: theme.spacing(),
}));

export const StyledAccordionSummary = styled(PoolAccordionSummary, {
    shouldForwardProp: prop => prop !== 'filterActive',
})<{
    filterActive?: boolean;
}>(({ theme, filterActive }) => ({
    minHeight: 62,
    padding: theme.spacing(1.5, 0, 1.5, 2),

    '&&.Mui-expanded': {
        backgroundColor: theme.palette.grey[200],
    },

    ...(filterActive && {
        backgroundColor: theme.palette.grey[200],
    }),
}));

const StyledDrawerHeader = styled('a')(({ theme }) => ({
    ...theme.typography.h5,
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    minHeight: theme.spacing(10),
    gap: theme.spacing(3),
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
}));

export interface AllFilterDrawerProps {
    open: boolean;
    totalResults?: number;
    disabledReset?: boolean;
    onDrawerClose: () => void;
}

export const AllFiltersDrawer = (props: PropsWithChildren<AllFilterDrawerProps>): ReactElement => {
    const { open = false, totalResults = 0, onDrawerClose, disabledReset, children } = props;
    const [searchParams, setSearchParams] = useSearchParams();

    const handleResetFilters = (e: MouseEvent<HTMLButtonElement>): void => {
        e.stopPropagation();

        for (const param in FilterParams) {
            searchParams.delete(FilterParams[param as keyof typeof FilterParams]);
        }

        setSearchParams(searchParams);
    };

    return (
        <PoolDrawer
            open={open}
            onClose={onDrawerClose}
            anchor="right"
            PaperProps={{ sx: { width: 367, position: undefined } }}
        >
            <StyledDrawerHeader onClick={onDrawerClose}>
                <PoolIconButton color="inherit" size="large">
                    <SortingArrowLeftIcon sx={{ fontSize: 24 }} />
                </PoolIconButton>
                <StyledPoolButton
                    variant="outlined"
                    color="primary"
                    onClick={handleResetFilters}
                    disabled={disabledReset}
                >
                    <FormattedMessage id="app.filter.resetFilters" />
                </StyledPoolButton>
            </StyledDrawerHeader>

            {children}

            <PoolRow sx={{ mt: 'auto', p: 2 }}>
                <PoolButton fullWidth={true} onClick={onDrawerClose}>
                    <FormattedMessage id="app.filter.apply" values={{ number: totalResults }} />
                </PoolButton>
            </PoolRow>
        </PoolDrawer>
    );
};
